import React, { useState } from 'react';
import { Box, FormControl } from '@mui/material';
import { useAuth } from '../../security/AuthProvider';
import {AuthErrorCode, getErrorMessage} from "../../api/types";
import { resendAccountSetupRequest } from '../../api/AuthApi';
import { Button } from '../../components/Button';
import { TextField } from '../../components/TextField';
import Spinner from '../../ui/Spinner';
import { ErrorAlert } from '../../components/ErrorAlert';
import { AuthFormContainer } from '../../components/AuthFormContainer';
import { SuccessAlert } from '../../components/SuccessAlert';

interface ForgotPasswordProps {}

const ForgotPassword: React.FC<ForgotPasswordProps> = () => {

    const { requestPasswordReset } = useAuth();
    const [username, setUsername] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setErrorCode(null);
        setSuccess(false);
        setLoading(true);

        try {
            const result = await requestPasswordReset(username);

            if (result.success) {
                setSuccess(true);
                setError(null);
                setErrorCode(null);
            } else {
                const code = result.errorCode || AuthErrorCode.UNEXPECTED_ERROR;
                setErrorCode(code);
                setError(getErrorMessage(code));
            }
        } catch (err) {
            console.error('Password reset request error:', err);
            setErrorCode(AuthErrorCode.UNEXPECTED_ERROR);
            setError(getErrorMessage(AuthErrorCode.UNEXPECTED_ERROR));
        } finally {
            setLoading(false);
        }
    };

    const handleResendAccountSetup = async () => {
        if (!username) return;
        setLoading(true);

        try {
            await resendAccountSetupRequest({ username });
        } catch (err) {
            console.error('Failed to resend account setup:', err);
        } finally {
            setLoading(false);
        }
    };

    const showResendLink = errorCode === AuthErrorCode.PENDING_USER_ACCOUNT_SETUP;

    if (loading) return <Spinner />;

    return (
        <AuthFormContainer title="Passwort zurücksetzen">
            {success && (
                <SuccessAlert message="Wir haben Ihnen eine E-Mail mit Anweisungen zum Zurücksetzen Ihres Passworts gesendet." />
            )}

            {error && (
                <ErrorAlert
                    message={error}
                    action={
                        showResendLink
                            ? {
                                label: 'Klicken Sie hier, um den Link erneut zu versenden',
                                onClick: handleResendAccountSetup
                            }
                            : undefined
                    }
                />
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, minWidth: '350px' }}>
                <FormControl fullWidth required sx={{ mt: 2 }}>
                    <TextField
                        id="email"
                        name="email"
                        placeholder="E-Mail-Adresse"
                        type="email"
                        value={username}
                        autoFocus={true}
                        onChange={(e) => setUsername(e)}
                        isDisabled={success}
                    />
                </FormControl>

                {!success && (
                    <Button
                        fullWidth={true}
                        type="submit"
                        variant="contained"
                        color="primary"
                        className="mt-4"
                        loading={loading}
                    >
                        Link anfordern
                    </Button>
                )}
            </Box>
        </AuthFormContainer>
    );
};

export default ForgotPassword;